import React, { useRef, useState, useEffect } from "react";
import {
  User,
  Upload,
  CalendarDays,
  ChevronDown,
  ShieldCheck,
  Save,
  X,
  RotateCcw
} from "lucide-react";
import MedicalHistory from "./MedicalHistory";
import { getProfileStepDetails, updateProfileStep } from "../../api/api";
import { updateUserProfileImage } from "../../api/storageService";

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg animate-slide-in ${type === "error" ? "bg-red-500" : type === "success" ? "bg-green-500" : "bg-blue-500"
      } text-white min-w-[300px]`}>
      <span className="text-sm flex-1">{message}</span>
      <button onClick={onClose} className="text-white hover:text-gray-200">×</button>
    </div>
  );
};


const indianStates = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry"
];

const PersonalInfo = ({ setActiveComponent }) => {
  const fileInputRef = useRef(null);
  const [profileImage, setProfileImage] = useState("");
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    name: "",
    gender: "",
    email: "",
    mobile: "",
    dateOfBirth: "",
    age: "",
    maritalStatus: "",
    bloodGroup: "",
    education: "",
    occupation: "",
    occupationalProfile: "",
    country: "",
    state: "",
    city: "",
    height: "",
    weight: "",
    bmi: "",
    bmiStatus: "",
    profilePicture: ""
  });

  const [formErrors, setFormErrors] = useState({});
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const datePickerRef = useRef(null);
  const [activeScreen, setActiveScreen] = useState("personal");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const addToast = (message, type = "success") => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const calculateBMI = (height, weight) => {
    if (height && weight && height > 0 && weight > 0) {
      const heightInMeters = height / 100;
      const bmiValue = (weight / (heightInMeters * heightInMeters)).toFixed(2);

      let bmiStatus = "";
      if (bmiValue < 18.5) bmiStatus = "Underweight";
      else if (bmiValue >= 18.5 && bmiValue < 25) bmiStatus = "Normal weight";
      else if (bmiValue >= 25 && bmiValue < 30) bmiStatus = "Overweight";
      else bmiStatus = "Obese";

      return { bmi: bmiValue, bmiStatus };
    }
    return { bmi: "", bmiStatus: "" };
  };

  const handleMeasurementChange = (field, value) => {
    const numValue = value.replace(/[^0-9.]/g, "");
    setFormData(prev => {
      const newData = { ...prev, [field]: numValue };
      if ((field === "height" || field === "weight") && newData.height && newData.weight) {
        const { bmi, bmiStatus } = calculateBMI(parseFloat(newData.height), parseFloat(newData.weight));
        newData.bmi = bmi;
        newData.bmiStatus = bmiStatus;
      }
      return newData;
    });
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        addToast("File size should be less than 5MB", "error");
        return;
      }
      if (!file.type.startsWith("image/")) {
        addToast("Please upload an image file", "error");
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target.result);
        setProfileImageFile(file);
        addToast("Profile photo updated successfully!", "success");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setProfileImage("");
    setProfileImageFile(null);
    addToast("Profile photo removed", "info");
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const validateMinimumAge = (dateOfBirth) => {
    if (!dateOfBirth) return true;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 8;
  };

  const isDateValid = (day, month, year) => {
    const today = new Date();
    const birthDate = new Date(year, month, day);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 8;
  };

  const validateIndianPhoneNumber = (number) => {
    const indianPhoneRegex = /^[6-9]\d{9}$/;
    return indianPhoneRegex.test(number);
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 10) value = value.slice(0, 10);
    setPhoneNumber(value);
    handleInputChange("mobile", value);

    if (value.length === 10) {
      if (validateIndianPhoneNumber(value)) {
        setPhoneError("");
      } else {
        setPhoneError("Enter a valid 10-digit Indian mobile number (starts with 6,7,8,9)");
      }
    } else if (value.length > 0 && value.length < 10) {
      setPhoneError("Mobile number must be 10 digits");
    } else {
      setPhoneError("");
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name?.trim()) errors.name = "Full name is required";
    if (!formData.email?.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }
    if (!formData.dateOfBirth) {
      errors.dateOfBirth = "Date of birth is required";
    } else if (!validateMinimumAge(formData.dateOfBirth)) {
      errors.dateOfBirth = "Age must be at least 8 years";
    }
    if (!formData.gender) errors.gender = "Gender is required";

    if (!phoneNumber) {
      errors.mobile = "Phone number is required";
    } else if (phoneNumber.length !== 10) {
      errors.mobile = "Phone number must be 10 digits";
    } else if (!validateIndianPhoneNumber(phoneNumber)) {
      errors.mobile = "Enter a valid Indian mobile number starting with 6,7,8,9";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const fetchProfileData = async () => {
    setIsLoading(true);
    try {
      const res = await getProfileStepDetails();
      console.log("GET Response:", res);

      if (res?.status) {
        const personalFields = res?.profile_completion?.steps?.personal?.fields;

        if (personalFields && personalFields.length > 0) {
          const fieldMap = {};
          personalFields.forEach((item) => {
            fieldMap[item.name] = item.value;
          });

          console.log("📋 Fetched Field Map:", fieldMap);

          let bmiStatus = "";
          if (fieldMap.bmi) {
            const bmiNum = parseFloat(fieldMap.bmi);
            if (bmiNum < 18.5) bmiStatus = "Underweight";
            else if (bmiNum >= 18.5 && bmiNum < 25) bmiStatus = "Normal weight";
            else if (bmiNum >= 25 && bmiNum < 30) bmiStatus = "Overweight";
            else bmiStatus = "Obese";
          }

          setFormData({
            title: fieldMap.title || "",
            name: fieldMap.name || "",
            gender: fieldMap.gender || "",
            email: fieldMap.email || "",
            mobile: fieldMap.mobile || "",
            dateOfBirth: fieldMap.dateOfBirth || "",
            age: fieldMap.age?.toString() || "",
            maritalStatus: fieldMap.maritalStatus || "",
            bloodGroup: fieldMap.bloodGroup || "",
            education: fieldMap.education || "",
            occupation: fieldMap.occupation || "",
            occupationalProfile: fieldMap.occupationalProfile || "",
            country: fieldMap.country || "",
            state: fieldMap.state || "",
            city: fieldMap.city || "",
            height: fieldMap.height?.toString() || "",
            weight: fieldMap.weight?.toString() || "",
            bmi: fieldMap.bmi?.toString() || "",
            bmiStatus: bmiStatus,
            profilePicture: fieldMap.profilePicture || ""
          });

          if (fieldMap.mobile) {
            setPhoneNumber(fieldMap.mobile.toString());
          }
          if (fieldMap.dateOfBirth) {
            const [year, month, day] = fieldMap.dateOfBirth.split("-");
            setDateOfBirth(`${day} / ${month} / ${year}`);
            setSelectedMonth(parseInt(month) - 1);
            setSelectedYear(parseInt(year));
          }

          if (fieldMap.profilePicture && fieldMap.profilePicture !== "null") {
            setProfileImage(fieldMap.profilePicture);
          } else {
            setProfileImage("");
          }
        }
      } else {
        console.error("Failed to fetch data:", res?.message);
      }
    } catch (error) {
      console.error("Error fetching profile data:", error);
      addToast("Failed to load profile data", "error");
    } finally {
      setIsLoading(false);
    }
  };

const handleSave = async () => {
  if (!validateForm()) {
    addToast("Please fill all required fields correctly", "error");
    return;
  }

  setIsSaving(true);

  try {
    const formDataToSend = new FormData();

    const fields = {
      title: formData.title || null,
      name: formData.name || null,
      gender: formData.gender || null,
      email: formData.email || null,
      mobile: phoneNumber || null,
      dateOfBirth: formData.dateOfBirth || null,
      maritalStatus: formData.maritalStatus || null,
      bloodGroup: formData.bloodGroup || null,
      education: formData.education || null,
      occupation: formData.occupation || null,
      occupationalProfile: formData.occupationalProfile || null,
      country: formData.country || null,
      state: formData.state || null,
      city: formData.city || null,
      height: formData.height ? parseFloat(formData.height) : null,
      weight: formData.weight ? parseFloat(formData.weight) : null,
      profilePicture: profileImageFile || null,
    };

    Object.keys(fields).forEach(key => {
      if (fields[key] !== null && fields[key] !== undefined && fields[key] !== "") {
        if (key === "profilePicture" && fields[key] instanceof File) {
          formDataToSend.append("profilePicture", fields[key]);
        } else if (key !== "profilePicture") {
          formDataToSend.append(key, fields[key]);
        }
      }
    });

    console.log("📤 Sending FormData with profile picture");

    const response = await updateProfileStep("personal", formDataToSend);

    console.log("Save Response:", response);

    if (response?.status) {
      let newImageUrl = null;
      
      // Update profile image from response
      if (response.data?.profilePicture) {
        let imageUrl = response.data.profilePicture;
        imageUrl = imageUrl.replace(/\/\//g, '/').replace('https:/', 'https://');
        newImageUrl = imageUrl;
        setProfileImage(imageUrl);
        console.log("✅ Profile image updated:", imageUrl);
        
        // ✅ UPDATE STORAGE WITH NEW IMAGE
        if (updateUserProfileImage(imageUrl)) {
          console.log("✅ Storage updated with new profile image");
          // Dispatch custom event to notify other components
          window.dispatchEvent(new CustomEvent('profileImageUpdated', { 
            detail: { imageUrl } 
          }));
        }
      }

      // Update form data from response
      if (response.data) {
        const data = response.data;
        setFormData(prev => ({
          ...prev,
          title: data.title || "",
          name: data.name || "",
          gender: data.gender || "",
          email: data.email || "",
          mobile: data.mobile || "",
          dateOfBirth: data.dateOfBirth || "",
          age: data.age?.toString() || "",
          maritalStatus: data.maritalStatus || "",
          bloodGroup: data.bloodGroup || "",
          education: data.education || "",
          occupation: data.occupation || "",
          occupationalProfile: data.occupationalProfile || "",
          country: data.country || "",
          state: data.state || "",
          city: data.city || "",
          height: data.height?.toString() || "",
          weight: data.weight?.toString() || "",
          bmi: data.bmi?.toString() || "",
          bmiStatus: data.bmiStatus || "",
          profilePicture: data.profilePicture || ""
        }));

        if (data.dateOfBirth) {
          const [year, month, day] = data.dateOfBirth.split("-");
          setDateOfBirth(`${day} / ${month} / ${year}`);
          setSelectedMonth(parseInt(month) - 1);
          setSelectedYear(parseInt(year));
        }

        if (data.mobile) {
          setPhoneNumber(data.mobile.toString());
        }
      }

      setProfileImageFile(null);
      addToast("Personal information saved successfully!", "success");

      setTimeout(() => {
        fetchProfileData();
      }, 500);

      setTimeout(() => {
        if (setActiveComponent) {
          setActiveComponent("medicalHistory");
        }
      }, 1500);
    } else {
      addToast(response?.message || "Failed to save data", "error");
    }
  } catch (error) {
    console.error("Save error:", error);
    addToast(error.message || "Failed to save personal information", "error");
  } finally {
    setIsSaving(false);
  }
};

  const handleCancel = () => {
    fetchProfileData();
    setPhoneError("");
    setFormErrors({});
    setProfileImageFile(null);
    addToast("Changes discarded", "info");
  };

  useEffect(() => {
    fetchProfileData();
  }, []);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const calculateAge = (day, month, year) => {
    const today = new Date();
    const birthDate = new Date(year, month, day);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleDateSelect = (day) => {
    if (!isDateValid(day, selectedMonth, selectedYear)) {
      addToast("Age must be at least 8 years", "error");
      return;
    }

    const formattedDate = `${day.toString().padStart(2, '0')} / ${(selectedMonth + 1).toString().padStart(2, '0')} / ${selectedYear}`;
    setDateOfBirth(formattedDate);
    const age = calculateAge(day, selectedMonth, selectedYear);
    const apiDate = `${selectedYear}-${(selectedMonth + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    setFormData(prev => ({ ...prev, dateOfBirth: apiDate, age }));
    setShowDatePicker(false);
    if (formErrors.dateOfBirth) {
      setFormErrors(prev => ({ ...prev, dateOfBirth: "" }));
    }
  };

  const handlePrevMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const handleYearChange = (e) => {
    const year = parseInt(e.target.value);
    setSelectedYear(year);
    setDateOfBirth("");
    setFormData(prev => ({ ...prev, dateOfBirth: "", age: "" }));
  };


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

 const getInitials = (name) => {
  if (!name || name.trim() === "") return "?";
  const nameParts = name.trim().split(" ");
  if (nameParts.length === 1) {
    return nameParts[0].charAt(0).toUpperCase();
  }
  // Return first character of first name and first character of last name
  const firstInitial = nameParts[0].charAt(0).toUpperCase();
  const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
  return firstInitial + lastInitial;
};

  const getAvatarColor = (name) => {
    return "#5bb532";
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#57b26a]"></div>
      </div>
    );
  }

  if (activeScreen === "medicalHistory") {
    return <MedicalHistory />;
  }

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const firstDay = getFirstDayOfMonth(selectedMonth, selectedYear);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-8 w-8"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = `${day.toString().padStart(2, '0')} / ${(selectedMonth + 1).toString().padStart(2, '0')} / ${selectedYear}`;
      const isSelected = dateOfBirth === currentDate;
      const isValid = isDateValid(day, selectedMonth, selectedYear);

      days.push(
        <button
          key={day}
          onClick={() => handleDateSelect(day)}
          disabled={!isValid}
          className={`h-8 w-8 rounded-full text-sm transition-colors ${!isValid
            ? "text-gray-300 cursor-not-allowed"
            : isSelected
              ? "bg-[#57b26a] text-white"
              : "text-[#24312d] hover:bg-[#57b26a] hover:text-white"
            }`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  return (
    <div className="font-lato">
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#24312d] mb-2">
            Personal Info
          </h2>
          <p className="text-[#98a19b] text-sm">
            Manage your personal details and how we can reach you.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleCancel}
            disabled={isSaving}
            className="h-[38px] px-4 border border-[#dfe5df] text-[#4d5954] rounded-md text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RotateCcw size={15} />
            Discard
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="h-[38px] px-4 bg-[#57b26a] text-white rounded-md text-sm flex items-center gap-2 hover:bg-[#4a9e5c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={15} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#e8ece8] rounded-xl p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5 pb-6 border-b border-[#edf1ed]">
       {profileImage && profileImage !== "" && profileImage !== "null" ? (
  <img
    src={profileImage}
    alt="profile"
    className="w-[72px] h-[72px] rounded-full object-cover"
    onError={(e) => {
      e.target.src = "";
      setProfileImage("");
    }}
  />
) : (
  <div
    className="w-[72px] h-[72px] rounded-full flex items-center justify-center text-white text-2xl font-semibold"
    style={{ backgroundColor: "#57b26a" }}
  >
    {getInitials(formData.name || "User")}  {/* This will now show both initials */}
  </div>
)}

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
            accept="image/*"
          />

          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <button
                onClick={handleUploadClick}
                className="h-[36px] px-4 font-lato border border-[#dfe5df] bg-[#f7f9f7] rounded-md text-[13px] text-[#24312d] hover:bg-gray-50 flex items-center gap-2"
              >
                <Upload size={14} />
                Upload New Photo
              </button>

              <button
                onClick={handleRemoveImage}
                className="text-[13px] text-[#ef4444] font-medium"
              >
                Remove
              </button>
            </div>
            <p className="text-[12px] text-[#98a19b]">
              Recommended size: 400x400px. Max size: 5MB.
            </p>
          </div>
        </div>

        <div className="pt-6">
          <div className="flex items-center gap-2 mb-5">
            <User size={15} className="text-green-500" />
            <h3 className="text-[15px] font-semibold text-[#24312d]">
              Basic Information
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                Title
              </label>
              <select
                value={formData.title || ""}
                onChange={(e) => handleInputChange("title", e.target.value)}
                className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none bg-white focus:border-[#57b26a] focus:ring-1 focus:ring-[#57b26a]"
                  disabled={!!formData.title}

              >
                <option value="" disabled>Select Title</option>
                <option value="Mr">Mr</option>
                <option value="Ms">Ms</option>
                <option value="Mrs">Mrs</option>
              </select>
            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name || ""}
                  disabled={!!formData.name}

                onChange={(e) => handleInputChange("name", e.target.value)}
                className={`w-full h-[42px] border rounded-md px-4 text-sm outline-none focus:ring-1 ${formErrors.name
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                  }`}
              />
              {formErrors.name && (
                <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                Date of Birth <span className="text-red-500">*</span>
              </label>
              <div className="relative" ref={datePickerRef}>
                <input
                  type="text"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  onFocus={() => setShowDatePicker(true)}
  disabled={!!formData.dateOfBirth}

                  className={`w-full h-[42px] border rounded-md px-4 pr-10 text-sm outline-none cursor-pointer focus:ring-1 ${formErrors.dateOfBirth
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                    }`}
                  placeholder="DD / MM / YYYY"
                />
                <CalendarDays
                  size={15}
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#98a19b] cursor-pointer hover:text-[#57b26a]"
                />
                {showDatePicker && (
                  <div className="absolute top-full left-0 mt-2 bg-white border border-[#e1e6e1] rounded-lg shadow-lg z-50 p-3 w-64">
                    <div className="flex items-center justify-between mb-3">
                      <button onClick={handlePrevMonth} className="p-1 hover:bg-gray-100 rounded-md">←</button>
                      <div className="flex gap-2">
                        <span className="text-sm font-medium text-[#24312d]">{months[selectedMonth]}</span>
                        <select
                          value={selectedYear}
                          onChange={handleYearChange}
                          className="text-sm border rounded-md px-2 py-1 focus:outline-none focus:border-[#57b26a]"
                        >
                          {Array.from({ length: 100 }, (_, i) => {
                            const year = new Date().getFullYear() - i;
                            // Calculate if this year would be valid (age >= 8)
                            const today = new Date();
                            const maxYear = today.getFullYear() - 8;
                            const isValid = year <= maxYear;

                            return (
                              <option
                                key={year}
                                value={year}
                                style={{ color: isValid ? '#24312d' : '#d1d6d2' }}
                                disabled={!isValid}
                              >
                                {year}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <button onClick={handleNextMonth} className="p-1 hover:bg-gray-100 rounded-md">→</button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 mb-2 text-center">
                      {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(day => (
                        <div key={day} className="text-xs text-[#98a19b]">{day}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-1">{renderCalendar()}</div>
                    <div className="mt-2 text-[10px] text-[#98a19b] text-center border-t border-[#e8ece8] pt-2">
                      * Age must be at least 8 years
                    </div>
                  </div>
                )}
              </div>
              {formErrors.dateOfBirth && (
                <p className="text-red-500 text-xs mt-1">{formErrors.dateOfBirth}</p>
              )}
            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                Age
              </label>
              <input
                type="text"
                value={formData.age || ""}
                disabled
                className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm bg-gray-50 text-[#98a19b]"
              />
            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                Gender <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={formData.gender || ""}
                    disabled={!!formData.gender}

                  onChange={(e) => handleInputChange("gender", e.target.value)}
                  className={`w-full h-[42px] border rounded-md px-4 appearance-none text-sm outline-none bg-white focus:ring-1 ${formErrors.gender
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1] focus:border-[#57b26a] focus:ring-[#57b26a]"
                    }`}
                >
                  <option value="" disabled>Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>

                <ChevronDown size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#98a19b] pointer-events-none" />
              </div>
              {formErrors.gender && (
                <p className="text-red-500 text-xs mt-1">{formErrors.gender}</p>
              )}
            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                Marital Status
              </label>
              <select
                value={formData.maritalStatus || ""}
                onChange={(e) => handleInputChange("maritalStatus", e.target.value)}
                className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none bg-white"
              >
                <option value="" disabled>Select Status</option>
                <option value="Married">Married</option>
                <option value="Single">Single</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
              </select>

            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                Blood Group
              </label>
              <select
                value={formData.bloodGroup || ""}
                onChange={(e) => handleInputChange("bloodGroup", e.target.value)}
                className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none bg-white"
              >
                <option value="" disabled>Select Blood Group</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
              </select>
            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                Education
              </label>
              <input
                type="text"
                value={formData.education || ""}
                onChange={(e) => handleInputChange("education", e.target.value)}
                placeholder="e.g., Postgraduate, Graduate, etc."
                className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none focus:border-[#57b26a]"
              />
            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                Occupation
              </label>
              <input
                type="text"
                value={formData.occupation || ""}
                onChange={(e) => handleInputChange("occupation", e.target.value)}
                placeholder="e.g., Business Owner, Homemaker, Student"
                className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none focus:border-[#57b26a]"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-[13px] text-[#24312d] mb-2">
                Occupational Profile
              </label>
              <textarea
                value={formData.occupationalProfile || ""}
                onChange={(e) => handleInputChange("occupationalProfile", e.target.value)}
                rows="2"
                placeholder="e.g., Works 9–5 in a sedentary desk job / work from home, etc."
                className="w-full border border-[#e1e6e1] rounded-md px-4 py-2 text-sm outline-none focus:border-[#57b26a] resize-none"
              />
            </div>
          </div>
        </div>

        <div className="pt-2 border-t border-[#edf1ed]">
          <div className="flex items-center gap-2 mb-5 pt-6">
            <ShieldCheck size={15} className="text-green-500" />
            <h3 className="text-[15px] font-semibold text-[#24312d]">
              Contact Information & Body Metrics
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.email || ""}
                disabled
                className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none bg-gray-100 cursor-not-allowed text-gray-500"
              />
              {formErrors.email && (
                <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#98a19b] text-sm">+91</span>
                <input
                  type="tel"
                  value={phoneNumber}
                  disabled
                  onChange={handlePhoneChange}
                  placeholder="9876543210"
                  maxLength="10"
                  className={`w-full h-[42px] border rounded-md pl-12 pr-4 text-sm outline-none bg-gray-100 cursor-not-allowed text-gray-500 ${formErrors.mobile || phoneError
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-[#e1e6e1]"
                    }`}
                />
              </div>
              {(phoneError || formErrors.mobile) && (
                <p className="text-red-500 text-xs mt-1">{phoneError || formErrors.mobile}</p>
              )}
            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                Country <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.country || "India"}
                disabled
                className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm bg-gray-100 cursor-not-allowed text-gray-700 font-medium"
              />
            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                State
              </label>

              <select
                value={formData.state || ""}
                onChange={(e) => handleInputChange("state", e.target.value)}
                className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none bg-white focus:border-[#57b26a] focus:ring-1 focus:ring-[#57b26a]"
              >
                <option value="" disabled>Select State</option>

                {indianStates.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                City
              </label>
              <input
                type="text"
                value={formData.city || ""}
                onChange={(e) => handleInputChange("city", e.target.value)}
                className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none focus:border-[#57b26a]"
              />
            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                Height (cm)
              </label>
              <input
                type="text"
                value={formData.height || ""}
                onChange={(e) => handleMeasurementChange("height", e.target.value)}
                placeholder="Enter height in cm"
                className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none focus:border-[#57b26a]"
              />
            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                Weight (kg)
              </label>
              <input
                type="text"
                value={formData.weight || ""}
                onChange={(e) => handleMeasurementChange("weight", e.target.value)}
                placeholder="Enter weight in kg"
                className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm outline-none focus:border-[#57b26a]"
              />
            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                BMI
              </label>
              <input
                type="text"
                value={formData.bmi || ""}
                disabled
                className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm bg-gray-50"
              />
            </div>

            <div>
              <label className="block text-[13px] text-[#24312d] mb-2">
                BMI Status
              </label>
              <input
                type="text"
                value={formData.bmiStatus || ""}
                disabled
                className="w-full h-[42px] border border-[#e1e6e1] rounded-md px-4 text-sm bg-gray-50"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalInfo;